'use client'

import { type ReactNode, useEffect, Fragment } from 'react'
import RefreshLivePreview from '@/live-preview/RefreshLivePreview'
import Container from '@/components/structure/Container'
import Tree from '@/components/content/sidebar/Tree'
import Section from '@/components/content/common/Section'
import BlogPostOverview from '@/components/content/blog/post/BlogPostOverview'
import BlogPostSidebar from '@/components/content/blog/post/BlogPostSidebar'
import BlogHero from '@/components/content/blog/root/BlogHero'
import BlogPostImage from '@/components/content/blog/post/BlogPostImage'
import ExploreButton from '@/components/content/common/ExploreButton'
import Breadcrumb from '@/components/ui/Breadcrumb'
import TabToggle from '@/components/navigation/Toggles'
import Timestamp from '@/components/ui/Timestamp'
import BestMarquee from '@/components/content/best/shared/BestMarquee'
import Authors from '@/components/content/common/Authors'
import RelatedComparisons from '@/components/content/common/RelatedComparisons'
import List from '@/components/content/common/List'
import ReviewThumbnail from '@/components/content/reviews/root/ReviewThumbnail'
import BlogSummary from '@/components/content/blog/post/BlogPostSummary'
import PreferredSource from '@/components/ui/PreferredSource'
import { useDealsTab, useComparisonsTab } from '@/components/content/common/Tabs'
import { Icon } from '@/icons/Icon'
import { PageContextProvider, usePageContext } from '@/components/content/common/PageContext'
import { generateStructure, renderContent } from '@/lib/content'
import type { Block, SectionItem } from '@/types/app'
import type { App, Blog, BlogAuthorsAuthor, Category, User } from '@/payload-types'

type BlogPostType = Pick<
  Blog,
  | 'id'
  | 'name'
  | 'slug'
  | 'blogApps'
  | 'blogCategories'
  | 'post'
  | 'isFeatured'
  | 'summary'
  | 'createdAt'
  | 'authors'
>

type BlogPostContainerProps = {
  blog: BlogPostType
  apps: App[]
  category?: Category
  layout?: 'standard' | 'editorial'
  modifiedTimestamp: string
  paywall?: boolean
}

type BlogPostProps = {
  blog: BlogPostType
  apps: App[]
  authors?: User[]
  breadcrumbs?: ReactNode
  content: SectionItem[]
  preamble?: ReactNode
  dealCount: number
  comparisonCount: number
  timestamps: {
    modified: string
    published: string
  }
  paywall?: boolean
}

export default function BlogPostContainer(props: BlogPostContainerProps) {
  return (
    <PageContextProvider scrollOffset={145} initialTab="Blog Post">
      <RefreshLivePreview />
      <BlogPost {...props} />
    </PageContextProvider>
  )
}

function BlogPost({
  blog,
  apps,
  category,
  layout = 'standard',
  modifiedTimestamp,
  paywall = false,
}: BlogPostContainerProps) {
  const { pageTab, setPageTab } = usePageContext()
  const { roots, preamble } = generateStructure({
    content: blog.post.root.children,
    includeHeading: layout === 'editorial',
  })

  const { items: deals, totalCount: totalDeals } = useDealsTab(apps)
  const {
    items: comparisons,
    totalCount: totalComparisons,
    comparisons: comparisonItems,
  } = useComparisonsTab(apps, 'blog')

  const publishedTimestamp = blog.createdAt
  const blogAuthors = blog.authors?.docs as BlogAuthorsAuthor[]
  const authors = blogAuthors?.map((blogAuthor) => blogAuthor.user) as User[]

  useEffect(() => {
    setPageTab('Blog Post')
  }, [setPageTab])

  // TLDR
  const tldrSection = [
    {
      title: `TLDR`,
      content: (
        <BlogSummary summary={blog.summary} className={layout === 'standard' ? 'mb-3' : ''} />
      ),
    },
  ]

  // Post
  const { sections: postSection, preamble: postPreamble } = renderContent({
    roots,
    preamble,
    indent: layout === 'editorial',
  })

  // Structure
  const content: SectionItem[] = (() => {
    switch (pageTab) {
      case 'Blog Post':
        return [...(layout === 'editorial' ? tldrSection : []), ...postSection]
      case 'Deals':
        return [...deals]
      case 'Comparison':
        return [...comparisons]
      default:
        return []
    }
  })()

  // Breadcrumbs
  const breadcrumbs = (
    <Breadcrumb
      trail={[
        {
          label: 'Blog',
          url: '/blog',
        },
        ...(category
          ? [
              {
                label: category.name,
                icon: category.icon,
                url: `/blog/#${category.slug}`,
              },
            ]
          : []),
      ]}
    />
  )

  return (
    <>
      {(() => {
        switch (layout) {
          case 'editorial':
            return (
              <FeaturePost
                blog={blog}
                apps={apps}
                authors={authors}
                breadcrumbs={breadcrumbs}
                content={content}
                preamble={postPreamble}
                dealCount={totalDeals}
                comparisonCount={totalComparisons}
                timestamps={{
                  modified: modifiedTimestamp,
                  published: publishedTimestamp,
                }}
                paywall={paywall}
              />
            )
          case 'standard':
          default:
            return (
              <StandardPost
                blog={blog}
                apps={apps}
                authors={authors}
                breadcrumbs={breadcrumbs}
                content={content}
                preamble={postPreamble}
                dealCount={totalDeals}
                comparisonCount={totalComparisons}
                timestamps={{
                  modified: modifiedTimestamp,
                  published: publishedTimestamp,
                }}
                paywall={paywall}
              />
            )
        }
      })()}

      {!paywall && (
        <Container reduced={true}>
          {totalComparisons > 0 && <RelatedComparisons comparisons={comparisonItems ?? []} />}
          <ExploreButton label="Explore all reviews" link="/apps" />
        </Container>
      )}
    </>
  )
}

export function StandardPost({
  blog,
  apps,
  authors,
  breadcrumbs,
  content,
  preamble,
  dealCount,
  comparisonCount,
  timestamps,
  paywall,
}: BlogPostProps) {
  const { pageTab } = usePageContext()

  return (
    <>
      <BlogHero apps={apps}>
        {breadcrumbs}
        <h1 className="text-balance text-dark-950 dark:text-white">{blog.name}</h1>
      </BlogHero>

      <article id="content">
        <Container
          sidebar={
            <>
              <Tree roots={content} header={<BlogPostOverview blog={blog} apps={apps} />} />

              <PreferredSource invert={true} />

              <div className="hidden flex-wrap items-center gap-y-2.5 md:flex">
                {(timestamps.modified || timestamps.published) && (
                  <Timestamp
                    modified={timestamps.modified}
                    published={timestamps.published}
                    lead="Last updated on"
                    trail="by"
                    className="pr-1"
                  />
                )}
                <Authors authors={authors} condensed={true} />
              </div>

              {!paywall && (
                <List
                  header={{
                    title: 'Mentioned in this post',
                    subtitle: 'Apps featured',
                    buttons: [{ label: 'See all', link: '/apps' }],
                    fullWidth: false,
                  }}
                  items={apps || []}
                  ListItem={({ item }: { item: App }) => (
                    <ReviewThumbnail reviewApp={item} hideButton={true} />
                  )}
                  className="hidden md:flex"
                />
              )}
            </>
          }
          mobilePadding={false}
        >
          <TabToggle
            defaultValue="Blog Post"
            defaultIcon={<Icon name="stub" />}
            deals={dealCount}
            comparisonCount={comparisonCount}
          />

          {/* {pageTab === 'Blog Post' && <BlogPostImage />} */}

          {preamble && pageTab === 'Blog Post' && <div className="px-5 pb-2">{preamble}</div>}

          {content.map((root, index) => {
            if (paywall && index > 0) {
              return null
            }

            return (
              <Fragment key={index}>
                <Section
                  title={root.title || ''}
                  isParent={root.children ? root.children.length > 0 : false}
                  isChild={root.depth !== 'root'}
                >
                  {root.content}
                </Section>
              </Fragment>
            )
          })}
        </Container>
      </article>
    </>
  )
}

export function FeaturePost({
  blog,
  apps,
  authors,
  breadcrumbs,
  content,
  preamble,
  dealCount,
  comparisonCount,
  timestamps,
  paywall,
}: BlogPostProps) {
  const { pageTab } = usePageContext()

  return (
    <>
      <BlogPostSidebar content={blog.post.root.children as Block[]} apps={apps} />

      <BlogHero apps={apps} type="editorial">
        <div className="mt-28 flex flex-col items-center gap-6 md:gap-10">
          <div className="flex items-center gap-8">
            {breadcrumbs}
            {(timestamps.modified || timestamps.published) && (
              <Timestamp modified={timestamps.modified} published={timestamps.published} />
            )}
          </div>
          <h1 className="text-balance text-center text-gray-950 transition-colors dark:text-white">
            {blog.name}
          </h1>
          <Authors authors={authors} />
          <BestMarquee apps={apps} theme="light" className="mt-2 md:mt-4" />
        </div>
      </BlogHero>

      <TabToggle
        defaultValue="Blog Post"
        defaultIcon={<Icon name="stub" />}
        deals={dealCount}
        comparisonCount={comparisonCount}
        center={true}
        primary={true}
      />

      <Container
        reduced={true}
        className="mb-6 flex flex-col gap-12 pr-0 sm:pr-16 md:mb-12 md:pr-0"
      >
        {pageTab === 'Blog Post' && <BlogPostImage />}

        <div
          id="content"
          className="rich-text relative mx-auto flex w-full max-w-content flex-col gap-6 px-2.5 md:gap-12 md:px-5"
        >
          {preamble && <div>{preamble}</div>}
          {content.map((root, index) => {
            if (paywall && index > 0) {
              return null
            }

            return (
              <div key={index} className="flex flex-col items-center gap-6">
                {root.content}
              </div>
            )
          })}
        </div>
      </Container>
    </>
  )
}
